import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import type {
  ArrivalsDTO,
  BoutiqueAdminDTO,
  BoutiqueDetailDTO,
  BoutiqueStockDTO,
  CampaignDTO,
  CampaignImpactDTO,
  ComposeInput,
  ConversationDTO,
  DashboardDTO,
  GroupDTO,
  HiboutikAccountDTO,
  InvitationDTO,
  ItApiKeyDTO,
  ItEntryDTO,
  ItOverviewDTO,
  ItProjectDTO,
  ItSettingsDTO,
  ItSyncResultDTO,
  MeDTO,
  MemberDTO,
  MessageDTO,
  MessagesPageDTO,
  NetworkStockDTO,
  NotificationDTO,
  ProductSheetDTO,
  SalesDTO,
  SyncStatusDTO,
  TaskAssignmentDTO,
  TaskTrackingDTO,
  AttachmentDTO,
} from "@easyshop/shared";
import { api } from "./api";
import { usePeriod } from "@/hooks/usePeriod";
import { apiParams } from "@/lib/period-nav";

/** Rafraîchissement « en direct » (messagerie, notifications) : polling doux. */
export const LIVE = { refetchInterval: 15_000, refetchOnWindowFocus: true } as const;

type Opts<T> = Omit<UseQueryOptions<T>, "queryKey" | "queryFn">;

export const useMe = (opts?: Opts<MeDTO>) =>
  useQuery({ queryKey: ["me"], queryFn: () => api.get<MeDTO>("/me"), refetchInterval: 60_000, ...opts });

function usePeriodParams() {
  const { period, cmp, date, range } = usePeriod();
  return apiParams({ period, cmp, date, range });
}

export function useDashboard(boutiqueIds?: string[]) {
  const p = usePeriodParams();
  return useQuery({
    queryKey: ["dashboard", p, boutiqueIds ?? []],
    queryFn: () => api.get<DashboardDTO>("/dashboard", { ...p, boutiqueIds }),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** CA du jour de chaque boutique (barre latérale PC), indépendant de la période choisie. */
export function useTodayDashboard(enabled = true) {
  return useQuery({
    queryKey: ["dashboard", "today"],
    queryFn: () => api.get<DashboardDTO>("/dashboard", { period: "jour", cmp: "j7" }),
    refetchInterval: 60_000,
    enabled,
  });
}

export function useBoutiqueOverview(id: string | undefined) {
  const p = usePeriodParams();
  return useQuery({
    queryKey: ["overview", id, p],
    queryFn: () => api.get<BoutiqueDetailDTO>(`/boutiques/${id}/overview`, p),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

export function useBoutiqueSales(id: string | undefined, page = 1) {
  const p = usePeriodParams();
  return useQuery({
    queryKey: ["sales", id, p, page],
    queryFn: () => api.get<SalesDTO>(`/boutiques/${id}/sales`, { ...p, page }),
    enabled: Boolean(id),
  });
}

export const useBoutiqueStock = (id: string | undefined) =>
  useQuery({ queryKey: ["stock", id], queryFn: () => api.get<BoutiqueStockDTO>(`/boutiques/${id}/stock`), enabled: Boolean(id) });

export const useBoutiqueArrivals = (id: string | undefined) =>
  useQuery({ queryKey: ["arrivals", id], queryFn: () => api.get<ArrivalsDTO>(`/boutiques/${id}/arrivals`), enabled: Boolean(id) });

export const useBoutiqueTasks = (id: string | undefined) =>
  useQuery({ queryKey: ["tasks", id], queryFn: () => api.get<TaskAssignmentDTO[]>(`/boutiques/${id}/tasks`), enabled: Boolean(id), ...LIVE });

export const useNetworkStock = (boutiqueIds?: string[], enabled = true) =>
  useQuery({
    queryKey: ["network-stock", boutiqueIds ?? []],
    queryFn: () => api.get<NetworkStockDTO>("/stock", { boutiqueIds }),
    enabled,
    placeholderData: keepPreviousData,
  });

export const useArrivals = (boutiqueIds?: string[]) =>
  useQuery({ queryKey: ["arrivals", "all", boutiqueIds ?? []], queryFn: () => api.get<ArrivalsDTO>("/arrivals", { boutiqueIds }) });

/** Fiche produit par identifiant interne ; `name` ne sert qu'aux anciens liens et aux produits pas encore rattachés. */
export const useProductSheet = (target: { id: string | null; name?: string | null } | null, focus?: string) =>
  useQuery({
    queryKey: ["product", target?.id ?? null, target?.id ? null : (target?.name ?? null), focus ?? null],
    queryFn: () =>
      api.get<ProductSheetDTO>("/products/sheet", target!.id ? { id: target!.id, focus } : { name: target!.name!, focus }),
    enabled: Boolean(target?.id || target?.name),
  });

export const useStaffMessage = (id: string | undefined) =>
  useQuery({
    queryKey: ["staff-message", id],
    queryFn: () => api.get<{ text: string; isOverride: boolean; date: string }>(`/boutiques/${id}/staff-message`),
    enabled: Boolean(id),
    retry: false,
    staleTime: 5 * 60_000,
  });

export const useConversations = (filter: string) =>
  useQuery({ queryKey: ["conversations", filter], queryFn: () => api.get<ConversationDTO[]>("/conversations", { filter }), ...LIVE });

export const useBoutiqueConversationId = (boutiqueId: string | undefined) =>
  useQuery({
    queryKey: ["conversation-id", boutiqueId],
    queryFn: () => api.get<{ id: string }>(`/boutiques/${boutiqueId}/conversation`),
    enabled: Boolean(boutiqueId),
    staleTime: Infinity,
  });

export const useMessages = (conversationId: string | undefined) =>
  useQuery({
    queryKey: ["messages", conversationId],
    queryFn: () => api.get<MessagesPageDTO>(`/conversations/${conversationId}/messages`, { limit: 60 }),
    enabled: Boolean(conversationId),
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });

export const useTaskTracking = () =>
  useQuery({ queryKey: ["tracking"], queryFn: () => api.get<TaskTrackingDTO[]>("/tasks/tracking", { limit: 20 }), ...LIVE });

export const useCampaigns = () => useQuery({ queryKey: ["campaigns"], queryFn: () => api.get<CampaignDTO[]>("/campaigns") });

export const useCampaign = (id: string | null) =>
  useQuery({
    queryKey: ["campaign", id],
    queryFn: () => api.get<CampaignDTO & { impact: CampaignImpactDTO }>(`/campaigns/${id}`),
    enabled: Boolean(id),
  });

export const usePromoInsights = (boutiqueIds?: string[]) =>
  useQuery({
    queryKey: ["promo-insights", boutiqueIds ?? []],
    queryFn: () => api.get<import("@easyshop/shared").PromoInsightsDTO>("/campaigns/insights", { boutiqueIds }),
    staleTime: 5 * 60_000,
  });

export const useNotifications = () =>
  useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<{ unread: number; items: NotificationDTO[] }>("/notifications"),
    ...LIVE,
  });

export const useSyncStatus = () => useQuery({ queryKey: ["sync"], queryFn: () => api.get<SyncStatusDTO>("/sync"), ...LIVE });

export const useGroups = () => useQuery({ queryKey: ["groups"], queryFn: () => api.get<GroupDTO[]>("/groups") });
export const useMembers = () => useQuery({ queryKey: ["members"], queryFn: () => api.get<MemberDTO[]>("/members") });
export const useInvitations = (enabled = true) =>
  useQuery({ queryKey: ["invitations"], queryFn: () => api.get<InvitationDTO[]>("/invitations"), enabled });
export const useHiboutikAccounts = () =>
  useQuery({ queryKey: ["hiboutik-accounts"], queryFn: () => api.get<HiboutikAccountDTO[]>("/hiboutik-accounts") });
export const useAdminBoutiques = (enabled = true) =>
  useQuery({ queryKey: ["admin-boutiques"], queryFn: () => api.get<BoutiqueAdminDTO[]>("/admin/boutiques"), enabled });

// --- Mutations ---------------------------------------------------------------

/** Personnalisation du réseau courant : nom, logo. `me` porte les deux. */
export function useNetworkBranding() {
  const qc = useQueryClient();
  const onSuccess = () => {
    void qc.invalidateQueries({ queryKey: ["me"] });
    void qc.invalidateQueries({ queryKey: ["platform-overview"] });
  };
  return {
    rename: useMutation({ mutationFn: (name: string) => api.patch("/organization", { name }), onSuccess }),
    uploadLogo: useMutation({ mutationFn: (file: File) => api.upload("/organization/logo", [file]), onSuccess }),
    removeLogo: useMutation({ mutationFn: () => api.del("/organization/logo"), onSuccess }),
  };
}

export function useSendMessage(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; attachmentIds?: string[] }) =>
      api.post<MessageDTO>(`/conversations/${conversationId}/messages`, input),
    onSuccess: (msg) => {
      qc.setQueryData<MessagesPageDTO>(["messages", conversationId], (old) =>
        old ? { ...old, messages: [...old.messages.filter((m) => m.id !== msg.id), msg] } : old
      );
      void qc.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => api.post(`/conversations/${conversationId}/read`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["conversations"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useCompose() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ComposeInput) => api.post<{ boutiques: number }>("/compose", input),
    onSuccess: () => {
      for (const k of ["conversations", "messages", "tracking", "tasks", "dashboard"]) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
}

export function useAssignmentAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; action: "seen" | "start" | "proof" | "done" | "validate" | "refuse" | "reopen"; proofAttachmentIds?: string[]; reason?: string }) => {
      const { id, ...body } = v;
      return api.post<TaskAssignmentDTO>(`/tasks/assignments/${id}`, body);
    },
    onSuccess: () => {
      for (const k of ["messages", "tracking", "tasks", "conversations", "dashboard"]) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
}

export const uploadFiles = (files: File[]) => api.upload<AttachmentDTO[]>("/attachments", files);

export function useSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: number; failed: number; syncedAt: string }>("/sync"),
    onSuccess: () => {
      for (const k of ["dashboard", "overview", "sync", "stock", "network-stock"]) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
}

export function useReadNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) => api.post("/notifications/read", { ids }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function usePlatformOverview() {
  const p = usePeriodParams();
  return useQuery({
    queryKey: ["platform-overview", p],
    queryFn: () => api.get<import("@easyshop/shared").PlatformOverviewDTO>("/platform/overview", p),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

export const usePlatformAdmins = () =>
  useQuery({
    queryKey: ["platform-admins"],
    queryFn: () => api.get<import("@easyshop/shared").PlatformAdminDTO[]>("/platform/admins"),
  });

export const usePlatformUsers = (status: "PENDING" | "APPROVED" | "REJECTED") =>
  useQuery({
    queryKey: ["platform-users", status],
    queryFn: () => api.get<import("@easyshop/shared").PlatformUserDTO[]>("/platform/users", { status }),
    ...LIVE,
  });

// --- Administration plateforme ---

export const useAdminStatus = () =>
  useQuery({
    queryKey: ["admin-status"],
    queryFn: () => api.get<import("@easyshop/shared").AdminEasyshopStatusDTO>("/platform/administration/status"),
  });

export const useAdminHiboutikAccounts = () =>
  useQuery({
    queryKey: ["admin-hiboutik-accounts"],
    queryFn: () => api.get<import("@easyshop/shared").AdminHiboutikAccountDTO[]>("/platform/administration/hiboutik-accounts"),
  });

export const usePlatformBoutiques = () =>
  useQuery({
    queryKey: ["platform-boutiques"],
    queryFn: () => api.get<import("@easyshop/shared").AdminBoutiqueDTO[]>("/platform/administration/boutiques"),
  });

export const useAdminEasyshopBoutiques = (enabled: boolean) =>
  useQuery({
    queryKey: ["admin-easyshop-boutiques"],
    queryFn: () => api.get<import("@easyshop/shared").AdminEasyshopBoutiqueDTO[]>("/platform/administration/easyshop/boutiques"),
    enabled,
  });

/** Les lancements à blanc que la caisse garde : relus toutes les 10 s tant que l'un tourne. */
export const useAdminInitialisations = (enabled: boolean) =>
  useQuery({
    queryKey: ["admin-initialisations"],
    queryFn: () => api.get<import("@easyshop/shared").AdminInitialisationResumeDTO[]>("/platform/administration/initialisations"),
    enabled,
    refetchInterval: (q) =>
      q.state.data?.some((t) => t.statut === "EN_COURS" || t.ecriture?.statut === "EN_COURS") ? 10_000 : false,
  });

/** Suit une initialisation à blanc : relue toutes les 2 s tant qu'elle tourne. */
export const useAdminInitialisation = (id: string | null) =>
  useQuery({
    queryKey: ["admin-initialisation", id],
    queryFn: () => api.get<import("@easyshop/shared").AdminInitialisationDTO>(`/platform/administration/initialisations/${id}`),
    enabled: id !== null,
    retry: false,
    refetchInterval: (q) =>
      q.state.data?.statut === "EN_COURS" || q.state.data?.ecriture?.statut === "EN_COURS"
        ? 2_000
        : // La suite de l'écriture (soldes, photos) dure des minutes : un regard toutes les 5 s suffit.
          q.state.data?.ecriture?.suite === "EN_COURS"
          ? 5_000
          : false,
  });

export const useAdminEasyshopReseaux = (enabled: boolean) =>
  useQuery({
    queryKey: ["admin-easyshop-reseaux"],
    queryFn: () => api.get<import("@easyshop/shared").AdminEasyshopReseauDTO[]>("/platform/administration/easyshop/reseaux"),
    enabled,
  });

// --- Planning des équipes ---

export function usePlanning(from: string, boutiqueIds: string[], days = 7) {
  return useQuery({
    queryKey: ["planning", from, days, boutiqueIds],
    queryFn: () => api.get<import("@easyshop/shared").PlanningDTO>("/planning", { from, days, boutiqueIds }),
    enabled: boutiqueIds.length > 0,
    placeholderData: keepPreviousData,
    ...LIVE,
  });
}

export const useMyPlanning = () =>
  useQuery({
    queryKey: ["planning-me"],
    queryFn: () => api.get<import("@easyshop/shared").MyPlanningDTO>("/planning/me"),
    staleTime: 60_000,
  });

/** Invalide tout ce qui dépend du planning après une modification. */
export function useInvalidatePlanning() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["planning"] }),
      qc.invalidateQueries({ queryKey: ["planning-me"] }),
    ]);
}


// --- Page IT : suivi des livraisons ---

export type ItEntryInput = Omit<ItEntryDTO, "id" | "source">;

/** Réglages du réseau : activer la page, jeton GitHub (jamais relu, seulement ses 4 derniers caractères). */
export function useItSettings(enabled = true) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["it-settings"], queryFn: () => api.get<ItSettingsDTO>("/it/settings"), enabled });
  const save = useMutation({
    mutationFn: (input: { enabled?: boolean; githubToken?: string | null }) => api.put<ItSettingsDTO>("/it/settings", input),
    onSuccess: (data) => {
      qc.setQueryData(["it-settings"], data);
      void qc.invalidateQueries({ queryKey: ["me"] });
      void qc.invalidateQueries({ queryKey: ["it"] });
    },
  });
  return { query, save };
}

export const useItOverview = () => useQuery({ queryKey: ["it"], queryFn: () => api.get<ItOverviewDTO>("/it"), refetchOnWindowFocus: true });

export function useItMutations() {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ["it"] });
  return {
    createProject: useMutation({ mutationFn: (input: { name: string; repo?: string }) => api.post<ItProjectDTO>("/it/projects", input), onSuccess: refresh }),
    updateProject: useMutation({
      mutationFn: ({ id, ...input }: { id: string; track?: boolean; repo?: string; name?: string }) => api.patch<ItProjectDTO>(`/it/projects/${id}`, input),
      onSuccess: refresh,
    }),
    deleteProject: useMutation({ mutationFn: (id: string) => api.del(`/it/projects/${id}`), onSuccess: refresh }),
    createEntry: useMutation({ mutationFn: (input: ItEntryInput) => api.post<ItEntryDTO>("/it/entries", input), onSuccess: refresh }),
    updateEntry: useMutation({ mutationFn: ({ id, ...input }: ItEntryInput & { id: string }) => api.patch<ItEntryDTO>(`/it/entries/${id}`, input), onSuccess: refresh }),
    deleteEntry: useMutation({ mutationFn: (id: string) => api.del(`/it/entries/${id}`), onSuccess: refresh }),
    sync: useMutation({ mutationFn: () => api.post<ItSyncResultDTO>("/it/sync"), onSuccess: refresh }),
  };
}

/** Clés d'accès des assistants (MCP). La clé en clair n'existe que dans la réponse de la création. */
export function useItApiKeys(enabled = true) {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ["it-keys"] });
  const query = useQuery({ queryKey: ["it-keys"], queryFn: () => api.get<ItApiKeyDTO[]>("/it/keys"), enabled });
  const create = useMutation({
    mutationFn: (input: { name: string; canWrite: boolean }) => api.post<ItApiKeyDTO & { key: string }>("/it/keys", input),
    onSuccess: refresh,
  });
  const revoke = useMutation({ mutationFn: (id: string) => api.del(`/it/keys/${id}`), onSuccess: refresh });
  return { query, create, revoke };
}
